import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { getAuthToken } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ChangePasswordPage — used in two modes:
 * 1. Forced (URL: /change-password?forced=1) — the dealer just signed in with
 *    a temp password an admin generated. `current_password` isn't needed
 *    (backend already gated the temp password), so we hide that field.
 * 2. Voluntary — dealer clicked "Change password" in settings. Current
 *    password IS required, backend enforces it.
 */
export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forced = searchParams.get('forced') === '1';

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setBusy(true);
    try {
      const token = getAuthToken();
      await axios.post(
        `${API}/auth/change-password`,
        forced ? { new_password: next } : { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password updated — welcome in.');
      navigate('/dealer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center px-6" data-testid="change-password-page">
      <form onSubmit={submit} className="w-full max-w-md bg-white/5 border border-white/10 rounded-2xl p-8 space-y-4">
        <h1 className="text-2xl font-bold">
          {forced ? 'Set your permanent password' : 'Change password'}
        </h1>
        <p className="text-sm text-white/60">
          {forced
            ? 'You signed in with a temporary password. Choose a new one to finish setup.'
            : 'Enter your current password, then pick a new one.'}
        </p>

        {!forced && (
          <div>
            <label className="text-xs font-semibold text-white/70">Current password</label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="mt-1 w-full rounded-md bg-white/10 border border-white/20 px-3 py-2 text-sm"
              data-testid="current-password-input"
            />
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-white/70">New password</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={6}
            className="mt-1 w-full rounded-md bg-white/10 border border-white/20 px-3 py-2 text-sm"
            data-testid="new-password-input"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white/70">Confirm new password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="mt-1 w-full rounded-md bg-white/10 border border-white/20 px-3 py-2 text-sm"
            data-testid="confirm-password-input"
          />
        </div>

        <button
          type="submit"
          disabled={busy}
          className="w-full py-3 rounded-full bg-[#F2A900] hover:bg-amber-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="change-password-submit"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {forced ? 'Set password & continue' : 'Update password'}
        </button>
      </form>
    </div>
  );
}
